/** Portail — demandes de devis : liste, nouvelle demande (préremplie par paramètres), détail, acceptation / refus / annulation. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Pagination, QuantityInput, SearchField, SelectField, Table, TextField, useToast } from '@myron/design-system';
import { Check, FilePlus2, Plus, Send, ShoppingCart, Trash2, X } from 'lucide-react';
import { get, post } from '../../../shared/api/client';
import { SiPermission } from '../../../shared/auth/session';
import { Confirmation, EntetePage, ErreurFormulaire, EtatRequete } from '../../../shared/ui/composants';
import { dateCourte, dateHeure, euros, LIBELLE_CONDITIONNEMENT } from '../../../shared/ui/format';
import {
  LIBELLE_ACTION_DEVIS, LIBELLE_STATUT_DEVIS, nouvelleCleIdempotence, TON_STATUT_DEVIS, useDevis, useMesDevis, useMutationsDevis,
  type DevisClient, type ProduitDevis, type TypeCond,
} from './api';

export function BadgeDevis({ statut }: { statut: string }) {
  return <Badge ton={TON_STATUT_DEVIS[statut] ?? 'neutre'}>{LIBELLE_STATUT_DEVIS[statut] ?? statut}</Badge>;
}

// ── Liste ────────────────────────────────────────────────────────────────────────────────────────
export function ListeMesDevis() {
  const [page, setPage] = useState(1);
  const q = useMesDevis(page);
  const nav = useNavigate();
  return (
    <div className="my-pile my-pile--large">
      <EntetePage
        titre="Mes devis"
        sousTitre="Demandez un prix pour un volume exceptionnel : MYRON vous répond avec une proposition datée."
        actions={<SiPermission permission="devis.demander"><Link to="/pro/devis/nouveau" className="my-btn my-btn--primaire"><FilePlus2 className="my-icon" aria-hidden />Nouvelle demande</Link></SiPermission>}
      />
      <EtatRequete
        requete={q}
        vide={(d) => d.total === 0}
        titreVide="Aucune demande de devis"
        texteVide="Pour un gros volume ou une quantité hors de vos conditions, envoyez une demande."
        enfants={(d) => (
          <>
            <Table<DevisClient>
              legende="Mes devis"
              cle={(x) => x.id}
              lignes={d.donnees}
              onLigne={(x) => nav(`/pro/devis/${x.id}`)}
              colonnes={[
                { cle: 'ref', titre: 'Référence', rendu: (x) => <Link to={`/pro/devis/${x.id}`} className="my-code">{x.reference}</Link> },
                { cle: 'date', titre: 'Demandé le', rendu: (x) => dateCourte(x.creeLe) },
                { cle: 'lignes', titre: 'Produits', rendu: (x) => `${x.lignes.length} ligne${x.lignes.length > 1 ? 's' : ''}` },
                { cle: 'statut', titre: 'Statut', rendu: (x) => <BadgeDevis statut={x.statut} /> },
                { cle: 'validite', titre: 'Valable jusqu’au', rendu: (x) => (x.valideJusqua ? dateCourte(x.valideJusqua) : '—') },
                { cle: 'total', titre: 'Total HT', num: true, rendu: (x) => euros(x.totalHtCentimes) },
              ]}
            />
            <Pagination page={d.page} total={d.total} taille={d.taille} onPage={setPage} />
          </>
        )}
      />
    </div>
  );
}

// ── Nouvelle demande ───────────────────────────────────────────────────────────────────────────────
type LigneSaisie = { cle: string; produit: ProduitDevis; conditionnementType: TypeCond; quantite: number };
let compteur = 0;

export function NouveauDevis() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const [lignes, setLignes] = useState<LigneSaisie[]>([]);
  const [message, setMessage] = useState('');
  const [recherche, setRecherche] = useState('');
  const prerempli = useRef(false);

  const produitInitial = params.get('produitId');
  const initial = useQuery({ queryKey: ['pro', 'devis', 'produits', 'ids', produitInitial], queryFn: () => get<ProduitDevis[]>('/pro/devis/produits', { ids: produitInitial! }), enabled: !!produitInitial });
  useEffect(() => {
    if (prerempli.current || !initial.data?.length) return;
    prerempli.current = true;
    const p = initial.data[0]!;
    const type = (p.conditionnements.find((c) => c.type === params.get('conditionnement'))?.type ?? p.conditionnements[0]?.type) as TypeCond | undefined;
    if (!type) return;
    const cond = p.conditionnements.find((c) => c.type === type)!;
    const q = Math.max(cond.minimum, Number(params.get('quantite')) || cond.minimum);
    setLignes([{ cle: `l${compteur++}`, produit: p, conditionnementType: type, quantite: q }]);
  }, [initial.data, params]);

  const resultats = useQuery({ queryKey: ['pro', 'devis', 'produits', recherche], queryFn: () => get<ProduitDevis[]>('/pro/devis/produits', { q: recherche }), enabled: recherche.trim().length >= 2 });

  const envoyer = useMutation({
    mutationFn: () => post<DevisClient>('/pro/devis', { lignes: lignes.map((l) => ({ produitId: l.produit.id, conditionnementType: l.conditionnementType, quantite: l.quantite })), ...(message.trim() ? { message: message.trim() } : {}) }),
    onSuccess: (d) => { toast({ ton: 'succes', titre: `Demande ${d.reference} envoyée`, texte: 'Vous serez prévenu dès que la proposition sera prête.' }); nav(`/pro/devis/${d.id}`); },
  });

  const ajouter = (p: ProduitDevis) => {
    const c = p.conditionnements[0];
    if (!c) return;
    setLignes((ls) => [...ls, { cle: `l${compteur++}`, produit: p, conditionnementType: c.type, quantite: c.minimum }]);
    setRecherche('');
  };
  const maj = (cle: string, champs: Partial<LigneSaisie>) => setLignes((ls) => ls.map((l) => (l.cle === cle ? { ...l, ...champs } : l)));

  return (
    <div className="my-pile my-pile--large">
      <EntetePage titre="Nouvelle demande de devis" fil={[{ libelle: 'Mes devis', vers: '/pro/devis' }, { libelle: 'Nouvelle demande' }]} sousTitre="Indiquez les produits, conditionnements et volumes souhaités. Aucun prix n’est engagé à ce stade." />
      {produitInitial && initial.isError && <Alert ton="alerte" titre="Produit indisponible">Le produit indiqué ne peut pas faire l’objet d’un devis.</Alert>}
      <Card as="section" aria-labelledby="t-produits">
        <div className="my-pile">
          <h2 id="t-produits" className="my-titre-3">Produits</h2>
          {lignes.length === 0 && <p className="my-secondaire">Aucun produit pour l’instant. Recherchez un produit ci-dessous.</p>}
          {lignes.map((l) => {
            const cond = l.produit.conditionnements.find((c) => c.type === l.conditionnementType)!;
            return (
              <div key={l.cle} className="my-form-ligne" data-testid="ligne-devis">
                <div className="my-pile my-pile--serre" style={{ flex: 2, minWidth: 200 }}>
                  <strong>{l.produit.libelle}</strong>
                  <span className="my-petit my-secondaire">{l.produit.sku} · minimum {cond.minimum}{cond.multiple > 1 ? `, par ${cond.multiple}` : ''}</span>
                </div>
                <SelectField
                  label="Conditionnement"
                  value={l.conditionnementType}
                  onChange={(e) => { const c = l.produit.conditionnements.find((x) => x.type === e.target.value)!; maj(l.cle, { conditionnementType: c.type, quantite: Math.max(c.minimum, l.quantite) }); }}
                  options={l.produit.conditionnements.map((c) => ({ value: c.type, label: `${LIBELLE_CONDITIONNEMENT[c.type]} (${c.unitesBase} u.)` }))}
                />
                <QuantityInput label={`Quantité — ${l.produit.libelle}`} valeur={l.quantite} min={cond.minimum} pas={cond.multiple} onChange={(v) => maj(l.cle, { quantite: v })} />
                <Button variante="tertiaire" compact icone={Trash2} aria-label={`Retirer ${l.produit.libelle}`} onClick={() => setLignes((ls) => ls.filter((x) => x.cle !== l.cle))}>Retirer</Button>
              </div>
            );
          })}
          <SearchField label="Ajouter un produit (nom, référence ou EAN)" value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Ex. riz, HUILE-OLIVE-1L…" />
          {recherche.trim().length >= 2 && (
            <EtatRequete
              requete={resultats}
              vide={(r) => r.length === 0}
              titreVide="Aucun produit trouvé"
              enfants={(r) => (
                <ul className="my-pile my-pile--serre" aria-label="Résultats de recherche">
                  {r.map((p) => (
                    <li key={p.id} className="my-ligne">
                      <span style={{ flex: 1 }}>{p.libelle} <span className="my-petit my-secondaire">{p.sku}</span></span>
                      <Button variante="secondaire" compact icone={Plus} onClick={() => ajouter(p)} aria-label={`Ajouter ${p.libelle}`}>Ajouter</Button>
                    </li>
                  ))}
                </ul>
              )}
            />
          )}
        </div>
      </Card>
      <Card as="section">
        <TextField label="Message pour MYRON (facultatif)" value={message} maxLength={2000} onChange={(e) => setMessage(e.target.value)} aide="Contexte, rythme de livraison souhaité, date limite…" />
      </Card>
      <ErreurFormulaire erreur={envoyer.error} />
      <div className="my-ligne">
        <Button icone={Send} disabled={lignes.length === 0} chargement={envoyer.isPending} onClick={() => envoyer.mutate()}>Envoyer la demande</Button>
        <Link to="/pro/devis" className="my-btn my-btn--tertiaire">Annuler</Link>
      </div>
    </div>
  );
}

// ── Détail ─────────────────────────────────────────────────────────────────────────────────────────
export function DetailDevis() {
  const { devisId = '' } = useParams();
  const q = useDevis(devisId);
  const m = useMutationsDevis(devisId);
  const toast = useToast();
  const nav = useNavigate();
  const cleAcceptation = useMemo(() => nouvelleCleIdempotence(), [devisId]);
  const [confirmer, setConfirmer] = useState<'accepter' | 'refuser' | 'annuler' | null>(null);

  return (
    <div className="my-pile my-pile--large">
      <EtatRequete
        requete={q}
        enfants={(d) => (
          <>
            <EntetePage titre={`Devis ${d.reference}`} fil={[{ libelle: 'Mes devis', vers: '/pro/devis' }, { libelle: d.reference }]} sousTitre={<>Demandé le {dateCourte(d.creeLe)} · <BadgeDevis statut={d.statut} /></>} />
            {d.statut === 'PROPOSE' && d.valideJusqua && <Alert ton="info" titre="Proposition reçue">Prix valables jusqu’au {dateCourte(d.valideJusqua)}. En acceptant, ces prix s’appliquent à vos commandes pour ces produits et les lignes sont ajoutées à votre panier.</Alert>}
            {(d.statut === 'DEMANDE' || d.statut === 'EN_ETUDE') && <Alert ton="info" titre="Demande en cours de traitement">MYRON étudie votre demande. Les prix apparaîtront ici dès la proposition.</Alert>}
            {d.statut === 'EXPIRE' && <Alert ton="alerte" titre="Proposition expirée">La durée de validité est dépassée. Vous pouvez envoyer une nouvelle demande.</Alert>}
            {d.statut === 'REFUSE' && d.motifRefus && <Alert ton="alerte" titre="Devis refusé">{d.motifRefus}</Alert>}
            {d.statut === 'ACCEPTE' && <Alert ton="succes" titre="Devis accepté" action={<Link to="/pro/panier" className="my-btn my-btn--secondaire my-btn--compact"><ShoppingCart className="my-icon" aria-hidden />Voir le panier</Link>}>Les prix du devis s’appliquent désormais à vos commandes de ces produits jusqu’au {dateCourte(d.valideJusqua)}.</Alert>}
            <Table
              legende={`Lignes du devis ${d.reference}`}
              cle={(l) => l.id}
              lignes={d.lignes}
              colonnes={[
                { cle: 'produit', titre: 'Produit', rendu: (l) => <span>{l.libelle} <span className="my-petit my-secondaire">{l.sku}</span></span> },
                { cle: 'cond', titre: 'Conditionnement', rendu: (l) => LIBELLE_CONDITIONNEMENT[l.conditionnementType] },
                { cle: 'qte', titre: 'Quantité', num: true, rendu: (l) => l.quantite },
                { cle: 'prix', titre: 'Prix unitaire HT', num: true, rendu: (l) => (l.prixProposeHtCentimes === null ? <span className="my-secondaire">En attente</span> : <span data-testid="prix-devis">{euros(l.prixProposeHtCentimes)}</span>) },
                { cle: 'total', titre: 'Total HT', num: true, rendu: (l) => euros(l.totalHtCentimes) },
              ]}
            />
            {d.totalHtCentimes !== null && <p className="my-titre-4" style={{ textAlign: 'right' }}>Total HT : <span className="my-num">{euros(d.totalHtCentimes)}</span></p>}
            {d.message && <Card><p className="my-petit my-secondaire">Votre message</p><p>{d.message}</p></Card>}
            <SiPermission permission="devis.demander">
              <div className="my-ligne">
                {d.peutAccepter && <Button icone={Check} onClick={() => setConfirmer('accepter')}>Accepter le devis</Button>}
                {d.peutAccepter && <Button variante="secondaire" icone={X} onClick={() => setConfirmer('refuser')}>Refuser</Button>}
                {d.peutAnnuler && <Button variante="tertiaire" icone={X} onClick={() => setConfirmer('annuler')}>Annuler la demande</Button>}
                {['EXPIRE', 'REFUSE', 'ANNULE'].includes(d.statut) && <Link className="my-btn my-btn--secondaire" to={`/pro/devis/nouveau?produitId=${d.lignes[0]?.produitId ?? ''}&conditionnement=${d.lignes[0]?.conditionnementType ?? ''}&quantite=${d.lignes[0]?.quantite ?? ''}`}>Nouvelle demande</Link>}
              </div>
            </SiPermission>
            <Card as="section" aria-labelledby="t-hist">
              <h2 id="t-hist" className="my-titre-4">Historique</h2>
              <ol className="my-pile my-pile--serre">
                {d.historique.map((h, i) => (
                  <li key={i} className="my-petit"><span className="my-secondaire">{dateHeure(h.date)}</span> — {LIBELLE_ACTION_DEVIS[h.action] ?? h.action}{h.auteur === 'MYRON' ? ' (MYRON)' : ''}</li>
                ))}
              </ol>
            </Card>
            <Confirmation
              ouverte={confirmer === 'accepter'}
              titre="Accepter ce devis ?"
              texte={`Les prix proposés (total ${euros(d.totalHtCentimes)} HT) deviennent vos conditions pour ces produits jusqu’au ${dateCourte(d.valideJusqua)} et les lignes sont ajoutées à votre panier. Le paiement reste à faire lors de la commande.`}
              libelleAction="Accepter et ajouter au panier"
              chargement={m.accepter.isPending}
              erreur={m.accepter.error}
              onFermer={() => setConfirmer(null)}
              onConfirmer={() => m.accepter.mutate(cleAcceptation, { onSuccess: (r) => { setConfirmer(null); toast({ ton: 'succes', titre: 'Devis accepté', texte: `${r.nbLignesPanier} ligne(s) dans votre panier.` }); nav('/pro/panier'); } })}
            />
            <Confirmation
              ouverte={confirmer === 'refuser'}
              titre="Refuser la proposition ?"
              texte="MYRON sera informé de votre refus. Vous pourrez envoyer une nouvelle demande."
              libelleAction="Refuser"
              danger
              motif="optionnel"
              chargement={m.refuser.isPending}
              erreur={m.refuser.error}
              onFermer={() => setConfirmer(null)}
              onConfirmer={(motif) => m.refuser.mutate(motif, { onSuccess: () => setConfirmer(null) })}
            />
            <Confirmation
              ouverte={confirmer === 'annuler'}
              titre="Annuler la demande ?"
              texte="La demande ne sera plus traitée."
              libelleAction="Annuler la demande"
              danger
              chargement={m.annuler.isPending}
              erreur={m.annuler.error}
              onFermer={() => setConfirmer(null)}
              onConfirmer={() => m.annuler.mutate(undefined, { onSuccess: () => setConfirmer(null) })}
            />
          </>
        )}
      />
    </div>
  );
}
